import { useNavigate, useLocation } from "react-router-dom";
import { MapPin, Phone, Clock } from "lucide-react";
import { Logo } from "./Logo";
import { RESTAURANT, HOURS } from "../data/site";
import { scrollToId } from "../lib/scroll";

const SECTION_LINKS = [
  { label: "Menu", id: "menu" },
  { label: "Catering", id: "catering" },
  { label: "Hours", id: "hours" },
  { label: "Reviews", id: "reviews" },
  { label: "Location", id: "location" },
];

export const Footer = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const goSection = (id) => {
    if (pathname !== "/") navigate(`/#${id}`);
    else scrollToId(id);
  };

  return (
    <footer data-testid="site-footer" className="bg-charcoal text-cream pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-1">
          <button onClick={() => navigate("/")} className="focus:outline-none">
            <Logo variant="light" />
          </button>
          <p className="mt-5 font-body text-cream/60 text-sm leading-relaxed">
            {RESTAURANT.cateringTagline}. Soul food with a Caribbean flair — made fresh and served like family in Hampton, VA by Chef Rainey.
          </p>
        </div>

        <div>
          <h4 className="font-heading text-lg text-gold mb-4">Explore</h4>
          <ul className="space-y-2.5">
            <li>
              <button
                data-testid="footer-link-about"
                onClick={() => navigate("/about")}
                className="font-body text-cream/70 hover:text-gold transition-colors text-sm"
              >
                Our Story
              </button>
            </li>
            {SECTION_LINKS.map((l) => (
              <li key={l.id}>
                <button
                  data-testid={`footer-link-${l.id}`}
                  onClick={() => goSection(l.id)}
                  className="font-body text-cream/70 hover:text-gold transition-colors text-sm"
                >
                  {l.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-heading text-lg text-gold mb-4">Find Us</h4>
          <a
            href={RESTAURANT.directionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-start gap-2.5 font-body text-cream/70 hover:text-gold transition-colors text-sm"
          >
            <MapPin size={17} className="mt-0.5 shrink-0" />
            <span>
              {RESTAURANT.address.line1}
              <br />
              {RESTAURANT.address.line2}
            </span>
          </a>
          <a
            href={RESTAURANT.phoneHref}
            className="mt-3 flex items-center gap-2.5 font-body text-cream/70 hover:text-gold transition-colors text-sm"
          >
            <Phone size={17} /> {RESTAURANT.phone}
          </a>
        </div>

        <div>
          <h4 className="font-heading text-lg text-gold mb-4 flex items-center gap-2">
            <Clock size={17} /> Hours
          </h4>
          <ul className="space-y-1.5">
            {HOURS.filter((h) => h.day !== 0 && h.day !== 1).map((h) => (
              <li key={h.label} className="flex justify-between font-body text-cream/70 text-sm">
                <span>{h.label.slice(0, 3)}</span>
                <span>{h.open} – {h.close}</span>
              </li>
            ))}
            <li className="flex justify-between font-body text-cream/40 text-sm pt-1">
              <span>Sun – Mon</span>
              <span>Closed</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-14 pt-6 border-t border-cream/10 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center">
        <p className="font-body text-cream/40 text-xs tracking-wide">
          © 2024 {RESTAURANT.legalName}. All Rights Reserved.
        </p>
        <span className="hidden sm:inline text-cream/20">·</span>
        <button
          data-testid="footer-owner-login"
          onClick={() => navigate("/admin")}
          className="font-body text-cream/40 hover:text-gold text-xs tracking-wide transition-colors"
        >
          Owner Login
        </button>
      </div>
    </footer>
  );
};
